import {
  APPLICATION_IDS,
  TOOL_IDS,
  TRIBUNALEA_APPLICATIONS,
  TRIBUNALEA_TOOLS,
} from "@tribunalea/shared";
import { SuiteOrganizationProfile } from "@tribunalea/auth/react";
import {
  ArrowRight,
  Building2,
  Coins,
  Files,
  History,
  LayoutTemplate,
  PanelsTopLeft,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { OrganizationAccessPanel } from "./OrganizationAccessPanel.jsx";
import {
  OrganizationBillingProfilePanel,
} from "./OrganizationBillingProfilePanel.jsx";
import { OrganizationBrandingPanel } from "./OrganizationBrandingPanel.jsx";

const actionLabels = {
  "member.added": "Integrante agregado",
  "member.removed": "Integrante removido",
  "member.invited": "Invitación enviada",
  "member.invitation_accepted": "Invitación aceptada",
  "member.invitation_revoked": "Invitación revocada",
  "member.role_changed": "Rol actualizado",
  "organization.identity_updated": "Identidad de la organización actualizada",
  "organization.deleted": "Organización eliminada en Clerk",
  "workspace.administrator_handoff_started": "Invitación administrativa enviada",
  "workspace.administrator_handoff_completed": "Administración de la organización transferida",
  "credits.monthly_granted": "Acreditación histórica de créditos",
  "credits.used": "Créditos utilizados",
  "credits.refunded": "Créditos reintegrados",
  "billing.profile_updated": "Datos de facturación actualizados",
  "billing.purchase_requested": "Ampliación solicitada",
  "billing.purchase_canceled": "Solicitud de ampliación cancelada",
  "billing.purchase_approved": "Ampliación aprobada",
  "billing.purchase_rejected": "Ampliación rechazada",
  "billing.invoice_created": "Factura registrada",
  "workspace.created": "Cuenta de organización creada",
  "workspace.policy_updated": "Configuración de la organización actualizada",
  "workspace.branding_updated": "Identidad del espacio actualizada",
  "interest_calculation.created": "Cálculo de intereses creado",
  "interest_calculation.archived": "Cálculo de intereses archivado",
  "interest_calculation.reviewed": "Cálculo de intereses revisado",
  "interest_calculation.approved": "Cálculo de intereses aprobado",
  "instrumenta_template.created": "Plantilla creada",
  "instrumenta_template.version_created": "Nueva versión de plantilla",
  "instrumenta_template.archived": "Plantilla archivada",
  "instrumenta_run.created": "Procesamiento iniciado",
  "instrumenta_run.reviewed": "Procesamiento revisado",
  "instrumenta_run.approved": "Procesamiento aprobado",
  "instrumenta_run.output_generated": "Resultado de procesamiento generado",
  "instrumenta_run.archived": "Procesamiento archivado",
  "institutional_report.generated": "Reporte de la organización generado",
  "workflow_template.created": "Plantilla de flujo creada",
  "workflow_template.updated": "Plantilla de flujo actualizada",
  "workflow_template.archived": "Plantilla de flujo archivada",
  "workflow_record.created": "Registro de flujo creado",
  "workflow_record.updated": "Registro de flujo actualizado",
  "workflow_record.archived": "Registro de flujo eliminado",
};

function formatDate(value) {
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function selectedIds(selection, catalog) {
  return selection?.mode === "selected"
    ? selection.allowedIds || []
    : catalog.map(({ id }) => id);
}

const praxisUrl = import.meta.env.VITE_PRAXIS_URL || (
  import.meta.env.DEV ? "http://localhost:5173" : "https://praxis.tribunalea.com"
);
const instrumentaUrl = import.meta.env.VITE_INSTRUMENTA_URL || (
  import.meta.env.DEV
    ? "http://localhost:5174"
    : "https://instrumenta.tribunalea.com"
);

export function InstitutionPage({ api, reloadAccess }) {
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([api.institutionSummary(), api.institutionActivity()])
      .then(([nextSummary, nextActivity]) => {
        if (!active) return;
        setSummary(nextSummary);
        setActivity(nextActivity.items);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [api]);

  const enabledApplications = useMemo(() => summary
    ? selectedIds(
        summary.workspace.accessPolicy.applications,
        TRIBUNALEA_APPLICATIONS,
      )
    : [], [summary]);
  const enabledTools = useMemo(() => summary
    ? selectedIds(summary.workspace.accessPolicy.tools, TRIBUNALEA_TOOLS)
    : [], [summary]);
  const memberNames = useMemo(() => new Map(
    (summary?.members || []).map((member) => [member.userId, member.name]),
  ), [summary]);

  if (loading) {
    return <div className="support-access-state">Cargando organización…</div>;
  }
  if (error || !summary) {
    return <div className="support-access-error">{error || "No pudimos abrir la organización."}</div>;
  }

  const {
    workspace,
    memberCapacity,
    credits,
    membership,
    members,
    billingProfile,
  } = summary;
  const memberCount = memberCapacity.maximum === null
    ? `${memberCapacity.occupied}`
    : `${memberCapacity.occupied}/${memberCapacity.maximum}`;
  return (
    <div className="institution-page">
      <header className="institution-heading">
        <span
          className="institution-logo"
          style={{ backgroundColor: workspace.branding.primaryColor }}
        >
          {workspace.branding.logoUrl ? (
            <img src={workspace.branding.logoUrl} alt="" />
          ) : <Building2 size={28} />}
        </span>
        <div>
          <span className="support-access-eyebrow">TRIBUNALEA ORGANIZACIONES</span>
          <h1>{workspace.displayName}</h1>
          <p>Espacio compartido, gobernado y auditable de la organización.</p>
        </div>
      </header>

      <section className="institution-summary-grid">
        <article><Users size={20} /><span><strong>{memberCount}</strong>Miembros en Clerk</span></article>
        <article><Coins size={20} /><span><strong>{credits.balance}</strong>Créditos disponibles</span></article>
        <article><PanelsTopLeft size={20} /><span><strong>{enabledApplications.length}</strong>Aplicaciones visibles</span></article>
        <article><ShieldCheck size={20} /><span><strong>{membership.role === "org:admin" ? "Administrador" : "Miembro"}</strong>Tu rol</span></article>
      </section>

      <section className="institution-onboarding">
        <header>
          <ShieldCheck size={20} />
          <div>
            <h2>Estás trabajando en un espacio compartido</h2>
            <p>
              Lo que crees mientras figure <strong>{workspace.displayName}</strong>
              {" "}en la barra superior será visible para la organización. Tu
              espacio Personal permanece separado.
            </p>
          </div>
        </header>
        <div>
          <a href={membership.canManage ? "#administrar-integrantes" : "#integrantes"}>
            <Users size={18} />
            <span><strong>{membership.canManage ? "Invitar al equipo" : "Ver el equipo"}</strong><small>Personas y roles del espacio</small></span>
            <ArrowRight size={15} />
          </a>
          {enabledApplications.includes(APPLICATION_IDS.PRAXIS) ? (
            <a href={`${praxisUrl.replace(/\/$/, "")}/expedientes`}>
              <Files size={18} />
              <span><strong>Crear el primer expediente</strong><small>Se comparte con la organización</small></span>
              <ArrowRight size={15} />
            </a>
          ) : null}
          {enabledTools.includes(TOOL_IDS.INSTRUMENTA_DOCUMENT_PROCESSOR) ? (
            <a href={`${instrumentaUrl.replace(/\/$/, "")}/procesador-documental`}>
              <LayoutTemplate size={18} />
              <span><strong>Procesar un documento</strong><small>Plantillas e historial compartidos</small></span>
              <ArrowRight size={15} />
            </a>
          ) : null}
        </div>
      </section>

      {membership.canManage ? (
        <OrganizationAccessPanel
          api={api}
          workspace={workspace}
          onUpdated={(updatedWorkspace) => {
            setSummary((current) => ({
              ...current,
              workspace: updatedWorkspace,
            }));
            reloadAccess?.();
          }}
        />
      ) : null}

      <div className="institution-content-grid">
        {!membership.canManage ? (
          <section className="institution-card">
            <header><h2>Accesos del espacio</h2><p>Configuración definida por la organización.</p></header>
            <div className="institution-access-list">
              {TRIBUNALEA_APPLICATIONS
                .filter(({ id }) => enabledApplications.includes(id))
                .map((application) => (
                  <div key={application.id}>
                    <strong>{application.name}</strong>
                    <span>{TRIBUNALEA_TOOLS
                      .filter(({ id, applicationId }) => (
                        applicationId === application.id && enabledTools.includes(id)
                      ))
                      .map(({ name }) => name)
                      .join(" · ") || "Todas sus herramientas"}</span>
                  </div>
                ))}
            </div>
          </section>
        ) : null}

        <section className="institution-card" id="integrantes">
          <header>
            <h2>Integrantes</h2>
            <p>
              {memberCapacity.maximum === null
                ? `${memberCapacity.occupied} miembros registrados en Clerk.`
                : `${memberCapacity.occupied} de ${memberCapacity.maximum} miembros permitidos por Clerk.`}
            </p>
          </header>
          <div className="institution-member-list">
            {members.map((member) => (
              <article key={member.id}>
                <span className="invitation-avatar">
                  {member.name.slice(0, 1).toUpperCase()}
                </span>
                <span><strong>{member.name}</strong><small>{member.identifier}</small></span>
                <em>{member.role === "org:admin" ? "Administrador" : "Miembro"}</em>
              </article>
            ))}
          </div>
        </section>

        <section className="institution-card institution-activity-card">
          <header><History size={19} /><div><h2>Actividad reciente</h2><p>Trazabilidad del espacio compartido.</p></div></header>
          <div className="institution-activity-list">
            {activity.length ? activity.map((event) => (
              <article key={event.id}>
                <span />
                <div><strong>{actionLabels[event.action] || event.action}</strong><small>{formatDate(event.occurredAt)}{event.actorUserId ? ` · ${memberNames.get(event.actorUserId) || event.actorUserId}` : ""}</small></div>
              </article>
            )) : <p>Todavía no hay actividad de la organización registrada.</p>}
          </div>
        </section>
      </div>

      {membership.canManage ? (
        <>
          <OrganizationBrandingPanel
            api={api}
            workspace={workspace}
            onUpdated={(updatedWorkspace) => {
              setSummary((current) => ({
                ...current,
                workspace: updatedWorkspace,
              }));
              reloadAccess?.();
            }}
          />
          <OrganizationBillingProfilePanel
            api={api}
            profile={billingProfile}
            onUpdated={(updatedProfile) => {
              setSummary((current) => ({
                ...current,
                billingProfile: updatedProfile,
              }));
            }}
          />
          <section className="institution-card institution-clerk-card" id="administrar-integrantes">
            <header><h2>Administrar integrantes</h2><p>Invitaciones, roles y membresías gestionadas de forma segura por Clerk.</p></header>
            <div className="clerk-organization-surface">
              <SuiteOrganizationProfile />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
